import { IconName } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { UserLogout } from "api/rpc";
import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useAppDispatch } from "hooks/redux";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import React, { useRef, useState } from "react";
import { NavLink, useHistory, useRouteMatch } from "react-router-dom";
import { showError } from "redux/actions/ui";
import { useTeeOnPortalContext } from "../TeeOnPortalContext";
import "./teeOnPortalNavigation.scss";

const { t } = i18next;

type TMenuOptions = {
  id: number;
  title: string;
  url: string;
  /**Determines if the link is opened in a new tab */
  newTab: boolean;
  icon?: IconName;
};

interface IState {
  profileDetailsOpen: boolean;
  menuOptionsOpen: boolean;
}

interface IProps {
  showNavOptions?: boolean;
}

const MENU_OPTIONS: Array<TMenuOptions> = [
  { id: 1, url: "/booking/teetimes/golf-courses", title: t("Golf Courses"), newTab: false, icon: "golf-flag-hole" },
];

export default function TeeOnPortalNavigation(props: IProps) {
  const { t } = useTranslation();
  const { showNavOptions } = props;
  const history = useHistory();
  const baseUrl = "/booking/teetimes/:facilityShortName";
  const facilityShortName = useRouteMatch<{ facilityShortName: string }>(baseUrl)?.params?.facilityShortName ?? "";
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const profileContainer = useRef<HTMLDivElement>();
  const menuContainer = useRef<HTMLDivElement>();
  const [state, setState] = useState<IState>({
    profileDetailsOpen: false,
    menuOptionsOpen: false,
  });

  async function logoutUser() {
    const logoutRes = await UserLogout(true);

    if (logoutRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error logging out")));
    }

    window.location.href = `/booking/teetimes${facilityShortName ? "/".concat(facilityShortName) : ""}`;
  }

  function navigateToURL(url: string, newTab = false) {
    if (newTab) {
      window.open(url);
    } else {
      if (window.location.href?.split("/")?.includes("golf-courses")) {
        history.push(url, {
          params: { golfCourses: 1 },
        });
        return;
      }
      history.push(url);
    }
  }

  useOutsideAlerter(profileContainer, () => {
    setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
  });

  useOutsideAlerter(menuContainer, () => {
    setState(prevState => ({ ...prevState, menuOptionsOpen: false }));
  });

  return (
    <div className="tee-on-portal-nav">
      {windowSize.width <= MOBILE_WIDTH && (state.menuOptionsOpen || state.profileDetailsOpen) && (
        <div
          className="tee-on-portal-menu-backdrop "
          onClick={() =>
            setState(prevState => ({ ...prevState, clientDetailsOpen: false, profileDetailsOpen: false }))
          }
        ></div>
      )}
      <div
        className="tee-on-portal-nav-sub-container"
        ref={menuContainer}
        onClick={
          windowSize.width <= MOBILE_WIDTH
            ? () => setState(prevState => ({ ...prevState, menuOptionsOpen: true }))
            : undefined
        }
      >
        <div className="tee-on-portal-nav-logo-container">
          <img className="tee-on-portal-nav-logo" src={"/public/images/Tee-On.png"} alt={t('Tee On Logo')} />
        </div>
        {windowSize.width <= MOBILE_WIDTH &&
          (state.menuOptionsOpen ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />)}
        <div
          className={classNames("tee-on-portal-profile-dropdown-container", {
            "display-user-info-dropdown": state.menuOptionsOpen,
          })}
        >
          <div className="dropdown-image-container">
            <div className="tee-on-portal-nav-logo-container">
              <img className="tee-on-portal-nav-logo" src={"/public/images/Tee-On.png"} alt={t('Tee On Logo')} />
            </div>

            <FontAwesomeIcon
              onClick={e => {
                e.stopPropagation();
                setState(prevState => ({ ...prevState, menuOptionsOpen: false }));
              }}
              icon={faXmark}
              className="profile-close-button"
              fixedWidth
            />
          </div>
          {MENU_OPTIONS?.map(option => {
            return (
              <div key={option.id} onClick={() => navigateToURL(option.url, option.newTab)} className="profile-action">
                <FontAwesomeIcon fixedWidth className="profile-action-icon" icon={["far", option.icon]} />
                <span>{option.title}</span>
              </div>
            );
          })}
        </div>

        {windowSize.width > MOBILE_WIDTH &&
          MENU_OPTIONS?.map(option => {
            return (
              <NavLink
                key={option.id}
                to={option.url}
                activeClassName="tee-on-portal-nav-menu-active"
                target={option?.newTab ? "_blank" : undefined}
              >
                {option.title}
              </NavLink>
            );
          })}
      </div>

      {teeOnPortalState?.user ? (
        <div
          className="tee-on-portal-user-info-parent-container"
          ref={profileContainer}
          onClick={() => setState(prevState => ({ ...prevState, profileDetailsOpen: true }))}
        >
          <p className="user-info-name">
            Hi, {teeOnPortalState?.user?.first_name}{" "}
            {teeOnPortalState?.user?.last_name ? `${teeOnPortalState?.user?.last_name?.charAt(0)?.toUpperCase()}.` : ""}
          </p>
          <div className="tee-on-portal-profile-image-container">
            {teeOnPortalState?.user?.profile_image_source ? (
              <img
                className="tee-on-portal-profile-image "
                src={teeOnPortalState?.user?.profile_image_source}
                alt={t('User profile image')}
              />
            ) : (
              <div className="tee-on-portal-profile-default-image">
                <p>
                  {teeOnPortalState?.user?.first_name?.charAt(0)?.toUpperCase()}
                  {teeOnPortalState?.user?.last_name?.charAt(0)?.toUpperCase()}
                </p>
              </div>
            )}
          </div>

          <div
            className={classNames("profile-dropdown-container", {
              "display-user-info-dropdown": state.profileDetailsOpen,
            })}
          >
            <FontAwesomeIcon
              onClick={e => {
                e.stopPropagation();
                setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
              }}
              icon={faXmark}
              className="profile-close-button"
            />
            <div className="profile-user-info-container">
              <div className="tee-on-portal-profile-image-container">
                {teeOnPortalState?.user?.profile_image_source ? (
                  <img
                    className="tee-on-portal-profile-image"
                    src={teeOnPortalState?.user?.profile_image_source}
                    alt={t('User profile image')}
                  />
                ) : (
                  <div className="tee-on-portal-profile-default-image">
                    <p>
                      {teeOnPortalState?.user?.first_name?.charAt(0)?.toUpperCase()}
                      {teeOnPortalState?.user?.last_name?.charAt(0)?.toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <p className="profile-user-name">Hello, {`${teeOnPortalState?.user?.full_name}`}</p>
                <p className="profile-user-email">{teeOnPortalState?.user?.email}</p>
              </div>
            </div>
            <div
              className="profile-actions-container"
              onClick={e => {
                e.stopPropagation();
                setState(prevState => ({ ...prevState, profileDetailsOpen: false }));
              }}
            >
              <div onClick={() => navigateToURL("/tee-on/portal/overview", true)} className="profile-action">
                <FontAwesomeIcon className="profile-action-icon" icon={["far", "user"]} />
                <span>{t('Account Overview')}</span>
              </div>
              <div onClick={logoutUser} className="profile-action">
                <FontAwesomeIcon className="profile-action-icon" icon={["far", "right-from-bracket"]} />
                <span>{t('Logout')}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="tee-on-portal-nav-sub-container">
          <Button size="medium" type="text" onClick={() => navigateToURL("/booking/login")}>
            {t('Log in')}
          </Button>
          <Button size="medium" type="secondary" onClick={() => navigateToURL("/booking/register")}>
            {t('Sign up')}
          </Button>
        </div>
      )}
    </div>
  );
}
